import { useState } from "react";
import "./TenantCard.css";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useTenantsApp } from "../../Context/TenantsApp/TenantsAppContext";
import { SET_TENANT_ID, DESELECT_ALL_TENANT_IDS, TOGGLE_DELETE_APP_DIALOG, TOGGLE_DISABLE_APP_DIALOG, DELETE_TENANT, ENABLE_TENANT, SELECT_TENANT_PROD, SELECT_TENANT_TEST, SET_INSTANCE_ID, TOGGLE_DELETE_APP_INSTANCE_DIALOG, TOGGLE_DISABLE_APP_INSTANCE_DIALOG, ENABLE_TENANT_INSTANCE } from "./TenantsAppEvents";
import TenantCardError from "./TenantCardError";
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { useTranslation } from "react-i18next";
import { getProject, updateProject } from "../../API/Project/project";
import { enableAllInstancesOfApplicationOnRuntime } from "../Helpers/Runtime/runtime";
import { useMsal } from "@azure/msal-react";
import { updateTenantProject } from "../../API/Tenant/Tenant";
import Loader from "../Shared/Loader/loader";
import { enableInstanceOnRuntime } from "../Helpers/Runtime/runtime";
import { updateProjectInstance } from "../../API/Project/project";

const SyncCard = ({ tenantId }) => {

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })
    const { state, dispatch } = useTenantsApp();

    const context = useMsal();
    const tenant = state.tenants.find(t => t._id === tenantId);
    const { tenantName, tenantOrganization, project, lastSyncedAt, syncStatus, error, syncToProd, syncToTest } = tenant;
    const { tenantProject, tenantProjectId, masterProjectId } = project || {};
    const { instances } = tenantProject || {};
    const isCurrentlySyncing = state.tenants.some(t => t.syncStatus === 'In Progress' || t.syncStatus === 'Queued');
    const tenantOwner = tenant.tenantUser || {};
    const shouldSyncDisabled = !tenant.tenantOrganizationId || !tenant.masterOrganizationId || !tenant.project.tenantProjectId || !tenant.project.masterProjectId;
    const [toggleShowErrorDialog, setToggleShowErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [inProgress, setInProgress] = useState(false);

    const handleSelectProd = (e) => {
        dispatch({ type: SELECT_TENANT_PROD, payload: { tenantId: tenant._id, value: !syncToProd } });
    };

    const handleSelectTest = (e) => {
        dispatch({ type: SELECT_TENANT_TEST, payload: { tenantId: tenant._id, value: !syncToTest } });
    };

    const handleDeleteApp = () => {
        dispatch({ type: SET_TENANT_ID, payload: tenant._id });
        dispatch({ type: DESELECT_ALL_TENANT_IDS });
        dispatch({ type: TOGGLE_DELETE_APP_DIALOG });
    }

    const handleDisableApp = () => {
        dispatch({ type: SET_TENANT_ID, payload: tenant._id });
        dispatch({ type: DESELECT_ALL_TENANT_IDS });
        dispatch({ type: TOGGLE_DISABLE_APP_DIALOG });
    }

    const handleDeleteAppInstance = (instanceId) => {
        dispatch({ type: SET_TENANT_ID, payload: tenant._id });
        dispatch({ type: DESELECT_ALL_TENANT_IDS });
        dispatch({ type: SET_INSTANCE_ID, payload: instanceId });
        dispatch({ type: TOGGLE_DELETE_APP_INSTANCE_DIALOG });
    }

    const handleDisableAppInstance = (instanceId) => {
        dispatch({ type: SET_TENANT_ID, payload: tenant._id });
        dispatch({ type: DESELECT_ALL_TENANT_IDS });
        dispatch({ type: SET_INSTANCE_ID, payload: instanceId });
        dispatch({ type: TOGGLE_DISABLE_APP_INSTANCE_DIALOG });
    }

    const handleEnableApp = async () => {
        setInProgress(true);
        const project = await getProject(tenantProjectId);
        if (!project) {
            dispatch({ type: DELETE_TENANT, payload: state.selectedTenantId });
            setInProgress(false);
            return;
        }

        const [disableStatus, disableMessage] = await enableAllInstancesOfApplicationOnRuntime(
            context, project.organizationId, project.instances, project.name
        );

        if (disableStatus === "error") {
            setInProgress(false);
            return;
        }

        await Promise.allSettled([
            updateProject(project._id, {
                isActive: true,
                deletedBy: null,
                deletedOn: null
            }),
            updateTenantProject(tenant._id, masterProjectId, { status: "accepted" })
        ]);

        dispatch({ type: ENABLE_TENANT, payload: { tenantId: tenant._id } });
        setInProgress(false);
    }

    const handleEnableAppInstance = async (instanceId) => {
        setInProgress(true);
        try {
            const project = await getProject(tenantProjectId);
            if (!project) {
                dispatch({ type: DELETE_TENANT, payload: state.selectedTenantId });
                setInProgress(false);
                return;
            }
            const instanceToEnable = project.instances.find(inst => inst._id === instanceId);
            if (!instanceToEnable) {
                console.error("Instance not found in project");
                setInProgress(false);
                return;
            }
            const response = await enableInstanceOnRuntime(context, project.organizationId, project.name, instanceToEnable._id);
            if (response.status === "Success") {
                await updateProjectInstance(project._id, instanceToEnable._id, { isActive: true });
                await dispatch({
                    type: ENABLE_TENANT_INSTANCE,
                    payload: {
                        tenantId: tenant._id,
                        instanceId: instanceToEnable._id,
                    }
                });
            }
        } catch (error) {
            console.error("Error enabling tenant app instance:", error);
        } finally {
            setInProgress(false);
        }
    }

    const statusClass = syncStatus === "In Progress" ? "bg-warning text-dark" : syncStatus === "Completed" ? "bg-success text-white" : "bg-danger text-white";

    return (
        <>
            {inProgress && <Loader />}
            <div className="card sync-card d-flex flex-column" >
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <div>
                        <div className="fw-semibold fs-5">
                            {tenantName}
                            <span className="text-muted">{" ("}{tenantOrganization.orgName}{")"}</span>
                        </div>
                        <div className="tenant-owner-info mb-2">
                            <div className="fs-6">
                                <span className="text-muted ms-1">
                                    {tenantOwner.displayName !== "" ? tenantOwner.displayName : tenantOwner.firstName !== "" ? tenantOwner.firstName + ' ' + tenantOwner.lastName : "N/A"}
                                </span>
                            </div>
                            <div className="fs-6">
                                <span className="text-muted ms-1">
                                    {tenantOwner.email || "N/A"}
                                </span>
                            </div>
                        </div>
                    </div>
                    <Menu
                        onSelect={(e) => e.item.data()}
                        className="no-dropdown-in-menu three-dots d-inline-block"
                    >
                        <MenuItem
                            disabled={isCurrentlySyncing || shouldSyncDisabled || inProgress}
                            cssClass="btn btn-secondary m-1 px-0 py-2 border-0"
                            data={() => { }}
                            linkRender={(props) => (
                                <span
                                    aria-label="extra-options"
                                    className="k-icon k-i-more-vertical g-0 m-0 px-3"
                                />
                            )}
                        >
                            <MenuItem
                                cssClass="bg-white hover-border-0"
                                text={t("delete_button")}
                                data={handleDeleteApp}
                            />
                            <MenuItem
                                cssClass="bg-white hover-border-0"
                                text={instances.some(instance => instance.isActive) ? t("disable_button") : t("enable_button")}
                                data={instances.some(instance => instance.isActive) ? handleDisableApp : handleEnableApp}
                            />
                        </MenuItem>
                    </Menu>
                </div>
                {/* Tenant-level Status */}
                {syncStatus && (
                    <div className="tenant-status-row mb-3 d-flex align-items-center">
                        <span className={`badge ${statusClass}`}>
                            {t(syncStatus?.toLowerCase().replace(/ /g, "_"))}
                        </span>
                        {error &&
                            <span className="badge bg-danger ms-2 text-white cursor-pointer float-end" onClick={() => {
                                setToggleShowErrorDialog(true);
                                setErrorMessage(error);
                            }}>
                                <i className="fas fa-exclamation-triangle me-1" />
                                {t("view_error")}
                            </span>
                        }
                        {toggleShowErrorDialog && (
                            <TenantCardError
                                error={errorMessage}
                                onClose={() => {
                                    setToggleShowErrorDialog(false);
                                    setErrorMessage("");
                                }}
                            />
                        )}
                    </div>
                )}



                {/* Instances List */}
                <div className="flex-grow-1">
                    {instances.map((inst) => {
                        return (
                            <div
                                onClick={
                                    inst.instanceType === "Production"
                                        ? handleSelectProd
                                        : handleSelectTest
                                }
                                className="instance-row cursor-pointer" key={inst._id}>
                                <Checkbox
                                    disabled={isCurrentlySyncing || inProgress || shouldSyncDisabled}
                                    id={`instance-checkbox-${inst._id}`}
                                    className="me-3 p-0 mt-0 m-0 instance-checkbox"
                                    checked={
                                        inst.instanceType === "Production"
                                            ? syncToProd
                                            : syncToTest
                                    }
                                    onChange={
                                        inst.instanceType === "Production"
                                            ? handleSelectProd
                                            : handleSelectTest
                                    }
                                />
                                <div className="instance-info">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="instance-label">
                                            {inst.instanceName}
                                            <span
                                                className={inst.isActive ? "badge-active float-end" : "badge-inactive float-end"}
                                            >
                                                {inst.isActive ? t("active") : t("not_active")}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="last-synced">
                                        {t("last_synced")}:{" "}
                                        {inst.updatedDate
                                            ? new Date(inst.updatedDate).toLocaleString()
                                            : t("never")}
                                    </div>
                                </div>
                                <Menu
                                    onSelect={e => {
                                        e.syntheticEvent.stopPropagation();
                                        e.item.data();
                                    }}
                                    className="no-dropdown-in-menu three-dots d-inline-block"
                                >
                                    <MenuItem
                                        disabled={isCurrentlySyncing || shouldSyncDisabled || inProgress}
                                        cssClass="btn btn-secondary px-0 py-2 border-0"
                                        data={() => { }}
                                        linkRender={(props) => (
                                            <span
                                                aria-label="extra-options"
                                                className="k-icon k-i-more-vertical g-0 m-0 px-0"
                                            />
                                        )}
                                    >
                                        <MenuItem
                                            cssClass="bg-white hover-border-0"
                                            text={t("delete_button")}
                                            data={() => handleDeleteAppInstance(inst._id)}
                                        />
                                        <MenuItem
                                            cssClass="bg-white hover-border-0"
                                            text={inst.isActive ? t("disable_button") : t("enable_button")}
                                            data={inst.isActive ?
                                                () => handleDisableAppInstance(inst._id)
                                                :
                                                () => handleEnableAppInstance(inst._id)
                                            }
                                        />
                                    </MenuItem>
                                </Menu>
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
};


export default SyncCard;